/**
 * useFormInput Hook
 *
 * Connects Input components to parent FormContainer via FormContext.
 * Falls back to local state when not inside a form.
 */

import { useState, useEffect, useCallback, useMemo, useId, useRef } from "react";
import { useFormContext } from "@/contexts/FormContext";

export interface UseFormInputOptions {
  /** Field name that maps to workflow variable or schema field */
  fieldName?: string;
  /** Initial value (used when not in form) */
  initialValue?: string;
  /** Called when value changes (always fired, even in form) */
  onChange?: (value: string) => void;
  /** Called when input loses focus (always fired, even in form) */
  onBlur?: () => void;
}

export interface UseFormInputReturn {
  /** Current value (from form context or local state) */
  value: string;
  /** Handler for value changes */
  handleChange: (value: string) => void;
  /** Handler for blur events */
  handleBlur: () => void;
  /** Current validation error (null if none) */
  error: string | null;
  /** Whether field has been touched */
  touched: boolean;
  /** Whether the input is inside a FormContainer */
  isInForm: boolean;
  /** Unique input ID */
  inputId: string;
  /** Clear the validation error */
  clearError: () => void;
}

/**
 * Hook for connecting inputs to FormContainer
 *
 * @example
 * ```tsx
 * function MyInput({ fieldName, placeholder }) {
 *   const { value, handleChange, handleBlur, error } = useFormInput({ fieldName });
 *
 *   return (
 *     <div>
 *       <input
 *         value={value}
 *         onChange={(e) => handleChange(e.target.value)}
 *         onBlur={handleBlur}
 *         placeholder={placeholder}
 *       />
 *       {error && <span className="error">{error}</span>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useFormInput({
  fieldName,
  initialValue = "",
  onChange,
  onBlur,
}: UseFormInputOptions = {}): UseFormInputReturn {
  // Get form context (null if not in form)
  const formContext = useFormContext();

  // Generate unique input ID
  const generatedId = useId();
  const inputId = `input-${fieldName || "unnamed"}-${generatedId}`;

  // Local state for when not in form
  const [localValue, setLocalValue] = useState(initialValue);
  const [localTouched, setLocalTouched] = useState(false);

  // Determine if we're inside a form with a valid field name
  const isInForm = formContext !== null && fieldName !== undefined && fieldName !== "";

  // Register/unregister input with form context
  // Use refs for the functions to avoid re-running when formContext reference changes
  const registerInputRef = useRef(formContext?.registerInput);
  const unregisterInputRef = useRef(formContext?.unregisterInput);

  // Keep refs updated
  useEffect(() => {
    registerInputRef.current = formContext?.registerInput;
    unregisterInputRef.current = formContext?.unregisterInput;
  });

  useEffect(() => {
    if (isInForm && fieldName && registerInputRef.current) {
      registerInputRef.current(fieldName, inputId);
      return () => {
        unregisterInputRef.current?.(fieldName);
      };
    }
  }, [isInForm, fieldName, inputId]); // Removed formContext from deps

  // Get value from appropriate source
  const value = useMemo(() => {
    if (isInForm && formContext && fieldName) {
      const formValue = formContext.getValue(fieldName);
      return formValue !== undefined && formValue !== null
        ? String(formValue)
        : "";
    }
    return localValue;
  }, [isInForm, formContext, fieldName, localValue]);

  // Handle value change
  const handleChange = useCallback(
    (newValue: string) => {
      if (isInForm && formContext && fieldName) {
        formContext.setValue(fieldName, newValue);
      } else {
        setLocalValue(newValue);
      }
      // Always call external onChange
      onChange?.(newValue);
    },
    [isInForm, formContext, fieldName, onChange]
  );

  // Handle blur
  const handleBlur = useCallback(() => {
    if (isInForm && formContext && fieldName) {
      formContext.setTouched(fieldName);
    } else {
      setLocalTouched(true);
    }
    // Always call external onBlur
    onBlur?.();
  }, [isInForm, formContext, fieldName, onBlur]);

  // Get error from form context (or null for local state)
  const error = useMemo(() => {
    if (isInForm && formContext && fieldName) {
      return formContext.getFieldError(fieldName);
    }
    return null;
  }, [isInForm, formContext, fieldName]);

  // Get touched state
  const touched = useMemo(() => {
    if (isInForm && formContext) {
      return formContext.state.touched[fieldName || ""] ?? false;
    }
    return localTouched;
  }, [isInForm, formContext, fieldName, localTouched]);

  // Clear error
  const clearError = useCallback(() => {
    if (isInForm && formContext && fieldName) {
      formContext.clearFieldError(fieldName);
    }
  }, [isInForm, formContext, fieldName]);

  return {
    value,
    handleChange,
    handleBlur,
    error,
    touched,
    isInForm,
    inputId,
    clearError,
  };
}

export default useFormInput;
